import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Home, RefreshCw, ExternalLink, Loader2, Pencil, Check, X, Layers, MapPin, Sparkles, Play } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const EDITABLE_FIELDS = [
  { key: "name", label: "Name" },
  { key: "domain", label: "Domain" },
  { key: "brand_name", label: "Brand name" },
  { key: "logo_url", label: "Logo URL" },
  { key: "monetization_type", label: "Monetization", select: ["hybrid", "pay_per_call", "affiliate"] },
  { key: "drip_threads_per_day", label: "Drip / day", type: "number" },
];

export default function OverviewTab({ project, onRefresh }) {
  const s = project.stats || {};
  const completion = s.threads_total ? Math.round((s.threads_published / s.threads_total) * 100) : 0;
  const [building, setBuilding] = useState(false);
  const [buildingHubs, setBuildingHubs] = useState(false);
  const [buildingGeo, setBuildingGeo] = useState(false);
  const [settingTier, setSettingTier] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});
  const [simToggling, setSimToggling] = useState(false);
  const [simRunning, setSimRunning] = useState(false);
  const [simLogs, setSimLogs] = useState([]);

  async function loadSimLogs() {
    try {
      const { data } = await api.get("/simulation/logs", { params: { project_id: project.id, limit: 5 } });
      setSimLogs(data);
    } catch { /* non-critical */ }
  }
  useEffect(() => { loadSimLogs(); /* eslint-disable-next-line */ }, [project.id]);

  async function toggleSimulation(checked) {
    setSimToggling(true);
    try {
      await api.patch(`/projects/${project.id}`, { simulation_enabled: checked });
      toast.success(checked ? "Community simulation enabled" : "Community simulation paused");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally { setSimToggling(false); }
  }

  async function runSimulationNow() {
    setSimRunning(true);
    try {
      const { data } = await api.post(`/simulation/run-now`, null, { params: { project_id: project.id } });
      toast.success(`Cycle done: +${data.replies_added} replies, ${data.votes_updated} vote bumps (${data.checked} checked)`);
      loadSimLogs(); onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Simulation cycle failed");
    } finally { setSimRunning(false); }
  }

  useEffect(() => {
    setDraft({
      name: project.name || "",
      domain: project.domain || "",
      brand_name: project.brand_name || "",
      logo_url: project.logo_url || "",
      monetization_type: project.monetization_type || "hybrid",
      drip_threads_per_day: project.drip_threads_per_day || 2,
    });
  }, [project]);

  async function rebuildHomepage() {
    setBuilding(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/homepage/build?set_as_front=true`);
      toast.success(`Homepage ${data.action} (page #${data.page_id})`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Homepage build failed");
    } finally {
      setBuilding(false);
    }
  }

  async function rebuildServiceHubs() {
    setBuildingHubs(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/service-hubs/build`);
      const built = (data.results || []).filter(r => r.page_id).length;
      toast.success(`Service hubs built: ${built}/${data.count}`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Service hubs build failed");
    } finally {
      setBuildingHubs(false);
    }
  }

  async function rebuildGeoHubs() {
    setBuildingGeo(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/geo-hubs/build`);
      toast.success(`Geo hubs built: ${data.states_count} states + ${data.cities_count} cities`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Geo hubs build failed");
    } finally {
      setBuildingGeo(false);
    }
  }

  async function setLaunchTier(tier) {
    setSettingTier(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/geo-hubs/set-launch-tier?tier=${tier}`);
      toast.success(`Tier ${tier} applied: ${data.published_count} published, ${data.drafted_count} drafted`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change tier");
    } finally {
      setSettingTier(false);
    }
  }

  async function saveEdits() {
    setSaving(true);
    try {
      const payload = { ...draft };
      payload.drip_threads_per_day = Number(payload.drip_threads_per_day) || 2;
      // empty strings → null so we don't store ""
      for (const k of Object.keys(payload)) {
        if (payload[k] === "") payload[k] = null;
      }
      await api.patch(`/projects/${project.id}`, payload);
      toast.success("Project updated");
      setEditing(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  function cancelEdit() {
    setEditing(false);
    setDraft({
      name: project.name || "",
      domain: project.domain || "",
      brand_name: project.brand_name || "",
      logo_url: project.logo_url || "",
      monetization_type: project.monetization_type || "hybrid",
      drip_threads_per_day: project.drip_threads_per_day || 2,
    });
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Project configuration</h3>
            {!editing ? (
              <button data-testid="project-edit-btn" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-border hover:border-primary hover:text-primary rounded-sm">
                <Pencil className="w-3 h-3" /> Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button data-testid="project-save-btn" onClick={saveEdits} disabled={saving} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 rounded-sm">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Save
                </button>
                <button onClick={cancelEdit} className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-border hover:border-red-600 hover:text-red-400 rounded-sm">
                  <X className="w-3 h-3" /> Cancel
                </button>
              </div>
            )}
          </div>

          {!editing ? (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["Name", project.name],
                ["Domain", project.domain || "—"],
                ["Brand name", project.brand_name || "—"],
                ["Logo URL", project.logo_url ? (
                  <span className="inline-flex items-center gap-2">
                    <img src={project.logo_url} alt="logo" className="w-6 h-6 object-contain rounded-sm border border-border bg-card" />
                    <span className="truncate">{project.logo_url}</span>
                  </span>
                ) : "—"],
                ["Pack", project.pack_slug],
                ["Monetization", project.monetization_type],
                ["Drip cadence", `${project.drip_threads_per_day} threads/day`],
                ["WP URL", project.wp_url || "—"],
                ["WP user", project.wp_username || "—"],
                ["WP configured", project.wp_configured ? "yes" : "no"],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[10px] txt-mono uppercase text-muted-foreground">{k}</dt>
                  <dd className="txt-mono mt-0.5 truncate">{v}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {EDITABLE_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <div className="text-[10px] txt-mono uppercase text-muted-foreground mb-1">{f.label}</div>
                  {f.select ? (
                    <select
                      data-testid={`edit-${f.key}`}
                      value={draft[f.key] ?? ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono"
                    >
                      {f.select.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <input
                      data-testid={`edit-${f.key}`}
                      type={f.type || "text"}
                      value={draft[f.key] ?? ""}
                      onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono"
                    />
                  )}
                  {f.key === "logo_url" && draft.logo_url && (
                    <img src={draft.logo_url} alt="preview" className="w-12 h-12 object-contain mt-2 border border-border bg-card rounded-sm" />
                  )}
                </label>
              ))}
              <div className="sm:col-span-2 text-xs text-muted-foreground">
                Pack slug, WP credentials and project ID are not editable here. Created : {project.created_at?.slice(0, 19).replace("T", " ")}
              </div>
            </div>
          )}
        </section>
        <section className="border border-border p-5">
          <h3 className="text-sm font-semibold mb-4">Publication progress</h3>
          <div className="stat-num text-4xl text-primary">{completion}%</div>
          <div className="text-xs text-muted-foreground mt-1">{s.threads_published || 0} of {s.threads_total || 0} generated threads published</div>
          <div className="mt-4 h-1 bg-border">
            <div className="h-1 bg-primary transition-all" style={{ width: `${completion}%` }} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-6">
            <div className="border border-border p-3">
              <div className="text-[10px] txt-mono uppercase text-muted-foreground">In queue</div>
              <div className="stat-num text-xl text-blue-400 mt-1">{s.publish_queue || 0}</div>
            </div>
            <div className="border border-border p-3">
              <div className="text-[10px] txt-mono uppercase text-muted-foreground">Clicks logged</div>
              <div className="stat-num text-xl text-emerald-400 mt-1">{s.clicks || 0}</div>
            </div>
          </div>
        </section>
      </div>

      <section className="border border-border p-5">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-9 h-9 grid place-items-center bg-primary/15 text-primary rounded-sm shrink-0">
            <Home className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold">Homepage</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Generates a SEO-optimized WP Page (Latest Discussions, Emergency Topics, Browse by Service &amp; State…) from current threads and sets it as the static front page.
            </p>
          </div>
          <button
            onClick={rebuildHomepage} disabled={building || !project.wp_configured}
            data-testid="rebuild-homepage-btn"
            className="inline-flex items-center gap-2 text-xs px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 rounded-sm transition-colors"
          >
            {building ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {project.homepage_wp_page_id ? "Rebuild homepage" : "Build homepage"}
          </button>
        </div>

        {project.homepage_wp_page_id ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div className="border border-border p-3">
              <div className="text-[10px] txt-mono uppercase text-muted-foreground">WP Page ID</div>
              <div className="txt-mono mt-1">#{project.homepage_wp_page_id}</div>
            </div>
            <div className="border border-border p-3">
              <div className="text-[10px] txt-mono uppercase text-muted-foreground">Last built</div>
              <div className="txt-mono mt-1">{project.homepage_built_at?.slice(0, 19).replace("T", " ") || "—"}</div>
            </div>
            <div className="border border-border p-3">
              <div className="text-[10px] txt-mono uppercase text-muted-foreground">Static front</div>
              <div className="txt-mono mt-1">{project.homepage_static_front_set ? <span className="text-emerald-400">yes</span> : <span className="text-muted-foreground">no</span>}</div>
            </div>
            <a
              href={project.homepage_wp_link || project.domain}
              target="_blank" rel="noopener noreferrer"
              className="border border-border p-3 hover:border-primary transition-colors group"
            >
              <div className="text-[10px] txt-mono uppercase text-muted-foreground flex items-center gap-1">
                Open <ExternalLink className="w-2 h-2 group-hover:text-primary" />
              </div>
              <div className="txt-mono mt-1 truncate text-primary">{(project.homepage_wp_link || project.domain || "").replace(/^https?:\/\//, "")}</div>
            </a>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">No homepage built yet. Click the button to generate one from your current threads.</div>
        )}
      </section>

      <section className="border border-border p-5">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-9 h-9 grid place-items-center bg-primary/15 text-primary rounded-sm shrink-0">
            <Layers className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold">Service hub pages</h3>
            <p className="text-xs text-muted-foreground mt-1">
              One SEO-optimized WP Page per service (Water Damage, Plumbing, HVAC…) listing related threads, cost discussions, cities and Pay-Per-Call CTA.
            </p>
          </div>
          <button
            onClick={rebuildServiceHubs} disabled={buildingHubs || !project.wp_configured}
            data-testid="rebuild-service-hubs-btn"
            className="inline-flex items-center gap-2 text-xs px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 rounded-sm transition-colors"
          >
            {buildingHubs ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {project.service_hubs_count ? "Rebuild service hubs" : "Build service hubs"}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
          <div className="border border-border p-3">
            <div className="text-[10px] txt-mono uppercase text-muted-foreground">Hubs built</div>
            <div className="txt-mono mt-1">{project.service_hubs_count || 0}</div>
          </div>
          <div className="border border-border p-3">
            <div className="text-[10px] txt-mono uppercase text-muted-foreground">Last built</div>
            <div className="txt-mono mt-1">{project.service_hubs_built_at?.slice(0, 19).replace("T", " ") || "—"}</div>
          </div>
          <div className="border border-border p-3">
            <div className="text-[10px] txt-mono uppercase text-muted-foreground">Scope</div>
            <div className="txt-mono mt-1">all services in pack</div>
          </div>
        </div>
      </section>

      <section className="border border-border p-5">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-9 h-9 grid place-items-center bg-primary/15 text-primary rounded-sm shrink-0">
            <MapPin className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold">Geo hub pages</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Localized SEO pages — 8 states + 15 cities each (128 hubs total). Cities are nested under their state and gated by launch tier.
            </p>
          </div>
          <button
            onClick={rebuildGeoHubs} disabled={buildingGeo || !project.wp_configured}
            data-testid="rebuild-geo-hubs-btn"
            className="inline-flex items-center gap-2 text-xs px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 rounded-sm transition-colors"
          >
            {buildingGeo ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {project.geo_hubs_count ? "Rebuild geo hubs" : "Build geo hubs"}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-4">
          <div className="border border-border p-3">
            <div className="text-[10px] txt-mono uppercase text-muted-foreground">Hubs built</div>
            <div className="txt-mono mt-1">{project.geo_hubs_count || 0} <span className="text-muted-foreground">/ 128</span></div>
          </div>
          <div className="border border-border p-3">
            <div className="text-[10px] txt-mono uppercase text-muted-foreground">Live (publish)</div>
            <div className="txt-mono mt-1 text-emerald-400">{project.geo_hubs_published_count ?? "—"}</div>
          </div>
          <div className="border border-border p-3">
            <div className="text-[10px] txt-mono uppercase text-muted-foreground">Drafted</div>
            <div className="txt-mono mt-1 text-muted-foreground">{project.geo_hubs_drafted_count ?? "—"}</div>
          </div>
          <div className="border border-border p-3">
            <div className="text-[10px] txt-mono uppercase text-muted-foreground">Last built</div>
            <div className="txt-mono mt-1">{project.geo_hubs_built_at?.slice(0, 19).replace("T", " ") || "—"}</div>
          </div>
        </div>

        <div className="border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs font-semibold">Launch tier</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                Controls how many city hubs are publicly published. Extras stay as WP drafts (invisible, re-publishable in 1 click).
              </div>
            </div>
            <div className="text-[10px] txt-mono uppercase text-muted-foreground">Current: <span className="text-primary">Tier {project.launch_tier ?? 4}</span></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {[
              { tier: 1, label: "Ultra-lean", cities: "3/state · 24 cities", total: "52 pages" },
              { tier: 2, label: "Expansion", cities: "6/state · 48 cities", total: "76 pages" },
              { tier: 3, label: "Moderate", cities: "10/state · 80 cities", total: "108 pages" },
              { tier: 4, label: "Full", cities: "15/state · 120 cities", total: "148 pages" },
            ].map(({ tier, label, cities, total }) => {
              const active = (project.launch_tier ?? 4) === tier;
              return (
                <button
                  key={tier}
                  onClick={() => setLaunchTier(tier)}
                  disabled={settingTier || !project.wp_configured || active}
                  data-testid={`launch-tier-${tier}-btn`}
                  className={`border p-3 text-left transition-colors rounded-sm ${
                    active ? "border-primary bg-primary/10" : "border-border hover:border-primary"
                  } disabled:opacity-50`}
                >
                  <div className="text-xs font-semibold flex items-center gap-1">
                    T{tier} · {label}
                    {active && <Check className="w-3 h-3 text-primary" />}
                  </div>
                  <div className="text-[10px] txt-mono text-muted-foreground mt-1">{cities}</div>
                  <div className="text-[10px] txt-mono text-muted-foreground">{total}</div>
                </button>
              );
            })}
          </div>
          {settingTier && (
            <div className="text-xs text-muted-foreground mt-3 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Flipping WP page statuses (this takes ~30-60s for 120 cities)…
            </div>
          )}
        </div>
      </section>

      <section className="border border-border p-5">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-9 h-9 grid place-items-center bg-primary/15 text-primary rounded-sm shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold">Community simulation</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Keeps published threads looking alive: ~1 cycle/day, each eligible thread has a ~20% chance of a new AI reply plus organic vote growth. Stops around 30-60 days after publish (max 15 replies/thread). Real visitor posts are never touched.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] txt-mono uppercase text-muted-foreground">{project.simulation_enabled ? "On" : "Paused"}</span>
            <Switch
              data-testid="simulation-enabled-toggle"
              checked={!!project.simulation_enabled}
              disabled={simToggling}
              onCheckedChange={toggleSimulation}
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs flex-1">
            {simLogs.length > 0 ? simLogs.map((l) => (
              <div key={l.id} className="border border-border p-3">
                <div className="text-[10px] txt-mono uppercase text-muted-foreground">{l.ran_at?.slice(0, 19).replace("T", " ")}</div>
                <div className="txt-mono mt-1">+{l.replies_added} replies · {l.votes_updated} vote bumps</div>
                <div className="text-muted-foreground">{l.checked} threads checked{l.errors ? ` · ${l.errors} errors` : ""}</div>
              </div>
            )) : (
              <div className="text-muted-foreground col-span-full">No simulation cycle has run yet.</div>
            )}
          </div>
          <button
            onClick={runSimulationNow} disabled={simRunning}
            data-testid="run-simulation-now-btn"
            className="inline-flex items-center gap-2 text-xs px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 rounded-sm transition-colors shrink-0"
          >
            {simRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
            Run cycle now
          </button>
        </div>
      </section>

    </div>
  );
}
